/**
 * CommunityJournalsCarousel - Infinite center-focused auto-scrolling carousel
 * 
 * Features:
 * - Center card is always in focus
 * - Partial cards visible on both left and right sides
 * - INFINITE LOOP - seamlessly wraps around
 * - Auto-slides every 4 seconds
 * - Responsive: 1 center card on mobile, 3+ on desktop
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useCommunityJournals, type CommunityJournal } from "@/hooks/useCommunityJournals";
import { User, Sparkles } from "lucide-react";

// Card dimensions
const CARD_WIDTH = 280;
const CARD_GAP = 16;
const SLIDE_AMOUNT = CARD_WIDTH + CARD_GAP;

/**
 * Individual card component for community journal
 */
const CommunityCard = ({
    journal,
    onClick,
}: {
    journal: CommunityJournal;
    onClick: () => void;
}) => {
    return (
        <div
            onClick={onClick}
            className="flex-shrink-0 bg-white/60 backdrop-blur-sm border border-slate-200/40 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 hover:bg-white/80 group"
            style={{ width: CARD_WIDTH }}
        >
            {/* Cover Image */}
            <div className="relative h-36 overflow-hidden">
                {journal.community_cover_url ? (
                    <img
                        src={journal.community_cover_url}
                        alt={journal.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                        <span className="text-4xl opacity-40">📓</span>
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>

            {/* Content */}
            <div className="p-4">
                <h4 className="font-medium text-base text-slate-800 truncate mb-2 group-hover:text-slate-900">
                    {journal.name}
                </h4>

                {/* Creator */}
                <div className="flex items-center gap-2 text-sm text-slate-500">
                    {journal.profiles?.avatar_url ? (
                        <img
                            src={journal.profiles.avatar_url}
                            alt=""
                            className="w-5 h-5 rounded-full"
                        />
                    ) : (
                        <User className="w-4 h-4" />
                    )}
                    <span className="truncate">
                        {journal.profiles?.full_name || 'Community'}
                    </span>
                </div>
            </div>
        </div>
    );
};

/**
 * Main carousel component - infinite loop
 */
export const CommunityJournalsCarousel = () => {
    const navigate = useNavigate();
    const { data: journals, isLoading } = useCommunityJournals();
    const containerRef = useRef<HTMLDivElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);

    // For infinite scroll, we duplicate the array and track position
    const [translateX, setTranslateX] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(true);
    const animationRef = useRef<number | null>(null);

    // Get the journals to display (limit to 10 for performance)
    const baseJournals = journals?.slice(0, 10) || [];

    // Triple the array for seamless infinite scroll (prev + current + next)
    const displayedJournals = baseJournals.length > 0
        ? [...baseJournals, ...baseJournals, ...baseJournals]
        : [];

    // Calculate initial position (center of middle set)
    const getInitialOffset = useCallback(() => {
        if (!containerRef.current || baseJournals.length === 0) return 0;
        const containerWidth = containerRef.current.offsetWidth;
        const centerOffset = (containerWidth - CARD_WIDTH) / 2;
        // Start at the beginning of the middle set
        const middleSetStart = baseJournals.length * SLIDE_AMOUNT;
        return centerOffset - middleSetStart;
    }, [baseJournals.length]);

    // Initialize position
    useEffect(() => {
        if (baseJournals.length > 0 && containerRef.current) {
            setTranslateX(getInitialOffset());
        }
    }, [baseJournals.length, getInitialOffset]);

    // Auto-slide effect - continuous left movement
    useEffect(() => {
        if (baseJournals.length === 0) return;

        const slide = () => {
            setIsTransitioning(true);
            setTranslateX(prev => prev - SLIDE_AMOUNT);
        };

        const interval = setInterval(slide, 4000);
        return () => clearInterval(interval);
    }, [baseJournals.length]);

    // Handle infinite loop reset
    useEffect(() => {
        if (baseJournals.length === 0 || !containerRef.current) return;

        const containerWidth = containerRef.current.offsetWidth;
        const centerOffset = (containerWidth - CARD_WIDTH) / 2;
        const middleSetStart = baseJournals.length * SLIDE_AMOUNT;
        const middleSetEnd = middleSetStart + (baseJournals.length * SLIDE_AMOUNT);

        // Current position relative to track start
        const currentPos = -translateX + centerOffset;

        // If we've scrolled past the middle set, jump back
        if (currentPos >= middleSetEnd) {
            // Wait for transition to complete, then reset without animation
            const timeout = setTimeout(() => {
                setIsTransitioning(false);
                setTranslateX(centerOffset - middleSetStart);
                // Re-enable transitions after a frame
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setIsTransitioning(true);
                    });
                });
            }, 700); // Match transition duration
            return () => clearTimeout(timeout);
        }
    }, [translateX, baseJournals.length]);

    const handleJournalClick = (journalId: string) => {
        navigate(`/community/journal/${journalId}`);
    };

    // Loading skeleton
    if (isLoading) {
        return (
            <div className="space-y-4">
                <div className="flex items-center justify-center gap-2 text-slate-500">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-medium">Explore Community Journals</span>
                </div>
                <div className="flex gap-4 overflow-hidden justify-center">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="flex-shrink-0 bg-slate-100/60 rounded-xl animate-pulse" style={{ width: CARD_WIDTH }}>
                            <div className="h-36 bg-slate-200/50 rounded-t-xl" />
                            <div className="p-4 space-y-2">
                                <div className="h-5 bg-slate-200/50 rounded w-3/4" />
                                <div className="h-4 bg-slate-200/50 rounded w-1/2" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Don't render if no community journals
    if (!journals || journals.length === 0) {
        return null;
    }

    return (
        <div className="space-y-6">
            {/* Section header - centered */}
            <div className="text-center space-y-2 px-4">
                <div className="flex items-center justify-center gap-2 text-slate-500">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-medium">Explore Community Journals</span>
                </div>
                <p className="text-xs text-slate-400">
                    Get inspired by what others are learning
                </p>
            </div>

            {/* Carousel container - overflow hidden with CSS mask for edge fade */}
            <div
                className="overflow-hidden"
                ref={containerRef}
                style={{
                    maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
                }}
            >
                {/* Cards track - infinite loop */}
                <div
                    ref={trackRef}
                    className="flex gap-4"
                    style={{
                        transform: `translateX(${translateX}px)`,
                        transition: isTransitioning ? 'transform 700ms ease-in-out' : 'none',
                    }}
                >
                    {displayedJournals.map((journal, index) => (
                        <CommunityCard
                            key={`${journal.id}-${index}`}
                            journal={journal}
                            onClick={() => handleJournalClick(journal.id)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CommunityJournalsCarousel;
